/**
 * NotificationBell — the one place an admin alert can land.
 *
 * Alerts were being written and never shown: the header carried a bell with a
 * permanently red dot and no wiring, so a rate waiting to be approved or a
 * transfer waiting to be answered reached nobody. This reads the signed-in
 * admin's own notifications, shows how many are unread, marks one read when it
 * is opened, and follows its link to the screen that can act on it.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuthStore } from '@/lib/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ago = (value) => {
  if (!value) return '';
  const then = new Date(value);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return then.toLocaleDateString();
};

export const NotificationBell = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const { data: count } = useQuery({
    queryKey: ['admin-unread-count'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/admin/notifications/unread-count`, { headers });
      if (!r.ok) throw new Error('unavailable');
      return r.json();
    },
    enabled: !!token,
    refetchInterval: 60000,
    retry: false,
  });

  const { data: items, isLoading } = useQuery({
    queryKey: ['admin-notifications'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/admin/my-notifications?limit=20`, { headers });
      if (!r.ok) throw new Error('unavailable');
      return r.json();
    },
    enabled: !!token && open,
    retry: false,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-unread-count'] });
    queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
  };

  const markOne = useMutation({
    mutationFn: async (id) => fetch(`${API_URL}/api/admin/notifications/${id}/mark-read`,
                                   { method: 'PUT', headers }),
    onSuccess: refresh,
  });

  const markAll = useMutation({
    mutationFn: async () => fetch(`${API_URL}/api/admin/notifications/mark-all-read`,
                                  { method: 'PUT', headers }),
    onSuccess: refresh,
  });

  const unread = count?.unread_count || 0;
  const list = Array.isArray(items) ? items : [];

  const openOne = (n) => {
    if (!n.read) markOne.mutate(n.id || n._id);
    const link = n.link || n.data?.link;
    if (link) {
      setOpen(false);
      navigate(link.replace(API_URL, ''));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative"
                data-testid="admin-notifications-btn">
          <Bell className="h-5 w-5" />
          {unread > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center
                             justify-center rounded-full bg-red-500 px-1 text-[10px]
                             font-semibold text-white"
                  data-testid="admin-notifications-count">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="admin-notifications-panel">
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <p className="text-sm font-semibold text-slate-800">
            Notifications{unread > 0 ? ` · ${unread} unread` : ''}
          </p>
          {unread > 0 ? (
            <Button variant="ghost" size="sm" className="h-7 text-xs"
                    onClick={() => markAll.mutate()}
                    data-testid="admin-notifications-mark-all">
              <CheckCheck className="mr-1 h-3.5 w-3.5" />Mark all read
            </Button>
          ) : null}
        </div>
        <ScrollArea className="max-h-96">
          {isLoading ? (
            <p className="flex items-center gap-2 px-4 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />Looking…
            </p>
          ) : !list.length ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500"
               data-testid="admin-notifications-empty">
              Nothing needs you right now.
            </p>
          ) : (
            list.map((n, i) => (
              <button
                key={n.id || n._id || i}
                type="button"
                onClick={() => openOne(n)}
                data-testid={`admin-notification-${i}`}
                className={`block w-full border-b px-4 py-3 text-left transition-colors
                            last:border-0 hover:bg-slate-50
                            ${n.read ? '' : 'bg-indigo-50/60'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-slate-800">
                    {n.title || n.notification_type}
                  </p>
                  <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                    {ago(n.created_at)}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-slate-600">{n.message || n.body}</p>
              </button>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
